import { useQuery } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { Loader2 } from "lucide-react";
import { api } from "../../api";
import { DESK } from "../../constants/testIds";

// The code members scan or type to check themselves in — put this on a screen at the entrance,
// or print it if the branch is in "static" mode. In "rotating" mode it re-fetches itself as each
// code expires, so what's on screen is always one the server will still accept.
export function GymCheckinCode({ branchId, compact = false }) {
  const codeQ = useQuery({
    queryKey: ["branch-checkin-code", branchId],
    queryFn: async () => (await api.get(`/branches/${branchId}/checkin-code`)).data,
    enabled: !!branchId,
    // Static codes never change, so don't poll for them.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.mode === "static") return false;
      return ((data.expires_in_seconds ?? 0) + 1) * 1000;
    },
  });

  if (!branchId) return null;
  if (codeQ.isLoading) {
    return <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin" color="#68686E" /></div>;
  }
  if (codeQ.isError) {
    return <p className="text-sm" style={{ color: "var(--scarlet-neg)" }}>Couldn't load the check-in code.</p>;
  }

  const data = codeQ.data;
  return (
    <div data-testid={DESK.gymCodePanel} className="flex flex-col items-center gap-3">
      <div className="p-3 rounded-xl bg-white" style={{ border: "1px solid #EBEAE5" }}>
        <QRCodeSVG value={data.qr_payload} size={compact ? 150 : 210} level="M" />
      </div>
      <div className="text-center">
        <p className="text-xs uppercase tracking-wide font-semibold" style={{ color: "#68686E" }}>Or enter this code</p>
        <p data-testid={DESK.gymCodePin} className="font-bold tabular-nums tracking-[0.25em]" style={{ fontSize: compact ? 26 : 34, color: "#141416" }}>
          {data.pin}
        </p>
        <p className="text-xs mt-1" style={{ color: "#68686E" }}>
          {data.mode === "static"
            ? "Fixed code — safe to print and put on the wall"
            : `Changes every ${data.rotation_seconds}s`}
        </p>
      </div>
    </div>
  );
}
